import axios from 'axios';
import { getActivePinia } from 'pinia';
import type { Ref } from 'vue';
import type {
    WatermarkType,
    WebsiteLogoOption
} from './cropper';

interface CropperWatermarkActionsContext
{
    websiteLogoLoading: Ref<boolean>;
    websiteLogo: Ref<HTMLImageElement | null>;
    websiteLogoOptions: Ref<WebsiteLogoOption[]>;
    selectedWebsiteLogoId: Ref<number | null>;
    websiteLogoObjectUrl: Ref<string | null>;
    watermarkLogo: Ref<HTMLImageElement | null>;
    watermarkEnabled: Ref<boolean>;
    watermarkType: Ref<WatermarkType>;
    watermarkText: Ref<string>;
    watermarkX: Ref<number>;
    watermarkY: Ref<number>;
    saveHistory: (label: string) => void;
    updatePreview: () => void;
    translate: (key: string) => string;
}

function loadLogo(src: string)
{
    return new Promise((resolve, reject) =>
    {
        const logo = new Image();

        logo.addEventListener('load', () => resolve(logo), { once: true });
        logo.addEventListener('error', reject, { once: true });
        logo.src = src;
    });
}

export function createCropperWatermarkActions(context: CropperWatermarkActionsContext)
{
    function enableWatermark()
    {
        if (!context.watermarkEnabled.value)
        {
            context.watermarkEnabled.value = true;
        }
    }

    function setWatermarkPosition(x: number, y: number)
    {
        context.watermarkX.value = x;
        context.watermarkY.value = y;
        enableWatermark();
        context.updatePreview();
        context.saveHistory(context.translate('[[[Zmiana pozycji znaku wodnego]]]'));
    }

    function isWatermarkPositionPresetActive(preset: { x: number; y: number })
    {
        return context.watermarkX.value === preset.x && context.watermarkY.value === preset.y;
    }

    function updateWatermarkControl()
    {
        enableWatermark();
        context.updatePreview();
        context.saveHistory(context.translate('[[[Zmiana znaku wodnego]]]'));
    }

    function setWatermarkType()
    {
        if (context.watermarkType.value === 'logo' && context.websiteLogo.value)
        {
            useWebsiteLogo(false);
        }

        updateWatermarkControl();
    }

    async function loadLogoFile(url: string)
    {
        if (context.websiteLogoObjectUrl.value)
        {
            URL.revokeObjectURL(context.websiteLogoObjectUrl.value);
            context.websiteLogoObjectUrl.value = null;
        }

        const response = await axios.get<Blob>(url, { responseType: 'blob' });

        context.websiteLogoObjectUrl.value = URL.createObjectURL(response.data);

        return await loadLogo(context.websiteLogoObjectUrl.value);
    }

    async function setWebsiteLogo(option: WebsiteLogoOption, saveSnapshot: boolean = true)
    {
        context.selectedWebsiteLogoId.value = option.id;
        context.websiteLogo.value = await loadLogoFile(baseurl(option.logoUrl));

        if (context.watermarkType.value === 'logo')
        {
            useWebsiteLogo(saveSnapshot);
        }
    }

    async function onWebsiteLogoChange()
    {
        const option = context.websiteLogoOptions.value.find(item => item.id === context.selectedWebsiteLogoId.value);

        if (!option)
            return;

        await setWebsiteLogo(option);
    }

    function getWebsiteIds(options: any[])
    {
        const pinia = getActivePinia();
        const current = pinia?.state?.value?.['cms-websites']?.current?.website;
        const ids = options.map(item => item.id);

        if (current)
        {
            return [current, ...ids.filter(item => item !== current)];
        }

        return ids;
    }

    async function loadWebsiteLogo()
    {
        try
        {
            context.websiteLogoLoading.value = true;
            context.websiteLogo.value = null;
            context.websiteLogoOptions.value = [];
            context.selectedWebsiteLogoId.value = null;

            const options = (await axios.get<any[]>('admin/cms/websites/options')).data;

            const websites = await Promise.all(getWebsiteIds(options).map(async websiteId => (await axios.get<Resource<any>>(`admin/cms/websites/${websiteId}`)).data.result));

            for (const website of websites)
            {
                const logoUrl = website?.logo?.url;

                if (!logoUrl)
                    continue;

                context.websiteLogoOptions.value.push({
                    id: website.id,
                    title: website.title,
                    logoUrl
                });
            }

            const firstLogo = context.websiteLogoOptions.value[0];

            if (firstLogo)
            {
                await setWebsiteLogo(firstLogo, false);
            }
        }
        catch
        {
            context.websiteLogo.value = null;
            context.websiteLogoOptions.value = [];
            context.selectedWebsiteLogoId.value = null;
        }
        finally
        {
            context.websiteLogoLoading.value = false;
        }
    }

    function useWebsiteLogo(saveSnapshot: boolean = true)
    {
        context.watermarkLogo.value = context.websiteLogo.value;
        context.watermarkType.value = 'logo';
        context.watermarkEnabled.value = true;
        context.updatePreview();

        if (saveSnapshot)
        {
            context.saveHistory(context.translate('[[[Ustawienie logo witryny jako znaku wodnego]]]'));
        }
    }

    function onWatermarkLogo(event: Event)
    {
        const target = event.target as HTMLInputElement;
        const file = target.files?.[0];

        if (!file)
            return;

        const reader = new FileReader();

        reader.addEventListener('load', async () =>
        {
            const logo = await loadLogo(reader.result as string);

            context.watermarkLogo.value = logo;
            context.watermarkType.value = 'logo';
            context.watermarkEnabled.value = true;
            context.updatePreview();
            context.saveHistory(context.translate('[[[Dodanie znaku wodnego]]]'));
        }, { once: true });
        reader.readAsDataURL(file);
    }

    return {
        enableWatermark,
        setWatermarkPosition,
        isWatermarkPositionPresetActive,
        updateWatermarkControl,
        setWatermarkType,
        onWebsiteLogoChange,
        loadWebsiteLogo,
        onWatermarkLogo
    };
}
